import { useState } from 'react';

import styled from 'styled-components';

import { FieldMappingSelectMultiValue } from '@components/field-mapping-select-multi';
import { FormValidatorType, useFormContext } from '@components/form';
import { FormError } from '@components/form-error';
import { FormInstance } from '@components/form/create-form';
import { useList, useListOptions } from '@components/integrations/lists';
import { SelectOption } from '@components/select';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { FieldMappingGroup, VariableValue } from '@components/text-editors/addons/field-mapping';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';
import { minutes } from '@utils/milliseconds';

import { useConditionComparators } from './comparators';
import {
  findMappedField,
  findValueOptions,
  findValueType,
  reconcileValueWithOptions,
  toVariableValue,
  withCustomValueOption,
} from './field-utils';

type ComparatorComponentProps<T> = {
  condition: {
    id?: string;
    field: VariableValue;
    comparator?: string;
    value?: any;
  };
  form: FormInstance<T>;
  variableGroups: FieldMappingGroup[];
  fieldMappingId?: string;
  isWebhook?: boolean;
  index?: number;
  elementIdKey?: keyof T;
  comparatorIdKey?: keyof T;
  valueIdKey?: keyof T;
  additionalComparators?: SelectOption[];
  filterComparators?: (comparators: SelectOption[]) => SelectOption[];
};

export const isMultiComparator = (c: string) => c === 'select_any_of' || c === 'select_none_of';

const EmptyDisabledInput = ({ label }: { label: string }) => <TextField label={label} isDisabled />;

export const ComparatorComponent = <T extends Record<string, any>>({
  condition,
  form: Form,
  variableGroups,
  fieldMappingId,
  isWebhook = false,
  index,
  elementIdKey,
  comparatorIdKey,
  valueIdKey,
  additionalComparators = [],
  filterComparators,
}: ComparatorComponentProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const { store } = useFormContext();
  const formValues = store.useFormValues();

  const { getComparator } = useConditionComparators();

  const BASE_COMPARATORS = [...additionalComparators, ...getComparator()];
  const DATE_COMPARATORS = [...additionalComparators, ...getComparator('date')];
  const FILE_COMPARATORS = [...additionalComparators, ...getComparator('file')];
  const BOOLEAN_COMPARATORS = [...additionalComparators, ...getComparator('boolean')];
  const NUMBER_COMPARATORS = [...additionalComparators, ...getComparator('number')];
  const SELECT_COMPARATORS = [...additionalComparators, ...getComparator('select')];
  const SELECT_MULTI_COMPARATORS = [...additionalComparators, ...getComparator('multi_select')];

  const validators = ['required'] as FormValidatorType[];

  const formValue = elementIdKey
    ? (formValues[elementIdKey as keyof typeof formValues] as VariableValue)
    : (formValues.field as VariableValue) || condition.field;

  const mappedField = findMappedField(formValue, variableGroups);
  const valueType = findValueType(mappedField);
  const inlineOptions = findValueOptions(mappedField);
  const listOptions = useListOptions(mappedField?.optionsFromListId, mappedField?.excludedOptions);
  const valueOptions =
    inlineOptions.length > 0 ? inlineOptions : withCustomValueOption(mappedField, listOptions);

  const [selectedComparator, setSelectedComparator] = useState<string>(condition.comparator ?? '');
  const [allowEmptyWebhook, setAllowEmptyWebhook] = useState(isWebhook && !condition.value);

  const isMulti = isMultiComparator(selectedComparator);
  const valueTypeChanged = isMulti !== isMultiComparator(condition.comparator ?? '');
  const valueFieldKey = isMulti ? 'multi' : 'single';
  const valueDefaultValue = valueTypeChanged ? undefined : condition.value;
  const reconciledValue = reconcileValueWithOptions(valueDefaultValue, valueOptions);
  const reconciledMultiValue = reconcileValueWithOptions(condition.value, valueOptions);

  const valueId = (valueIdKey ?? 'value') as any;
  const valueTestid = `${valueType}-condition-value-${index}`;
  const liveValue = formValues[valueId as keyof typeof formValues];
  const singleValueReconciled = reconcileValueWithOptions(
    liveValue === undefined ? valueDefaultValue : liveValue,
    valueOptions,
  );
  const isSingleValueStale = singleValueReconciled.isStale;
  const multiValueReconciled = reconcileValueWithOptions(
    liveValue === undefined ? condition.value : liveValue,
    valueOptions,
  );
  const isMultiValueStale = multiValueReconciled.isStale;

  const listQuery = useList(mappedField?.optionsFromListId ?? '', { staleTime: minutes(5) });
  const isListResolved =
    !mappedField?.optionsFromListId || listQuery.isFetched || listQuery.isError;

  const WebhookAllowEmpty = () =>
    isWebhook ? (
      <Switch
        label={t('fieldmapInJourney')}
        key={`allow-${String(allowEmptyWebhook)}`}
        onChange={() => setAllowEmptyWebhook(!allowEmptyWebhook)}
        defaultValue={allowEmptyWebhook}
      />
    ) : null;

  const allowEmpty =
    allowEmptyWebhook ||
    selectedComparator.includes('empty') ||
    selectedComparator.includes('changes_value');

  const applyFilter = (comparators: SelectOption[]) => {
    return filterComparators ? filterComparators(comparators) : comparators;
  };

  const comparatorId = (comparatorIdKey ?? 'comparator') as any;

  const wrappedValue = toVariableValue(condition.value);
  const defaultVariableValue = wrappedValue.value === '' ? undefined : wrappedValue;
  const wrappedReconciledValue = toVariableValue(reconciledValue.value);
  const reconciledVariableValue =
    wrappedReconciledValue.value === '' ? undefined : wrappedReconciledValue;

  const textValueField = (
    <Form.FieldMappingTextEditor
      dataTestid={valueTestid}
      id={valueId}
      label={t('valueLabel')}
      variableGroups={variableGroups}
      defaultValue={defaultVariableValue}
      validators={validators}
      singleLine
      plaintext
      fieldMappingId={fieldMappingId ?? ''}
    />
  );

  switch (valueType) {
    case 'date':
      return (
        <Stack space={4}>
          <Stack space={2}>
            <Form.Select
              id={comparatorId}
              options={applyFilter(DATE_COMPARATORS)}
              label={t('comparatorLabel')}
              defaultValue={condition.comparator}
              validators={validators}
              isClearable={false}
              onChange={setSelectedComparator}
            />
            {allowEmpty ? (
              <EmptyDisabledInput label={t('valueLabel')} />
            ) : (
              <Form.FieldMappingDatePicker
                dataTestid={valueTestid}
                id={valueId}
                label={t('dateLabel')}
                variableGroups={variableGroups}
                defaultValue={defaultVariableValue}
                validators={validators}
                fieldMappingId={fieldMappingId ?? ''}
              />
            )}
          </Stack>
          <WebhookAllowEmpty />
        </Stack>
      );
    case 'select':
      return (
        <Stack space={4}>
          <ComparatorValueContainer>
            <Form.Select
              id={comparatorId}
              options={applyFilter(SELECT_COMPARATORS)}
              label={t('comparatorLabel')}
              defaultValue={condition.comparator}
              validators={validators}
              isClearable={false}
              onChange={setSelectedComparator}
            />
            {allowEmpty ? (
              <EmptyDisabledInput label={t('valueLabel')} />
            ) : isMulti ? (
              <Form.FieldMappingSelectMulti
                dataTestid={valueTestid}
                key={`${valueFieldKey}${isListResolved ? '' : '-loading'}`}
                id={valueId}
                label={t('valuesLabel')}
                options={valueOptions}
                variableGroups={variableGroups}
                fieldMappingId={fieldMappingId ?? ''}
                validators={validators}
                defaultValue={reconciledValue.value as FieldMappingSelectMultiValue}
              />
            ) : (
              <Form.FieldMappingSelectField
                dataTestid={valueTestid}
                key={`${valueFieldKey}${isListResolved ? '' : '-loading'}`}
                id={valueId}
                label={t('valueLabel')}
                variableGroups={variableGroups}
                defaultValue={reconciledVariableValue}
                validators={validators}
                fieldMappingId={fieldMappingId ?? ''}
                options={valueOptions}
              />
            )}
            {!allowEmpty && isSingleValueStale && (
              <FormError
                text={
                  isMulti
                    ? t('staleValues', { values: singleValueReconciled.staleLabels.join(', ') })
                    : t('staleValue')
                }
              />
            )}
          </ComparatorValueContainer>
          <WebhookAllowEmpty />
        </Stack>
      );
    case 'multi_select':
      return (
        <Stack space={4}>
          <ComparatorValueContainer>
            <Form.Select
              id={comparatorId}
              options={applyFilter(SELECT_MULTI_COMPARATORS)}
              label={t('comparatorLabel')}
              defaultValue={condition.comparator}
              validators={validators}
              isClearable={false}
              onChange={setSelectedComparator}
            />
            {allowEmpty ? (
              <EmptyDisabledInput label={t('valueLabel')} />
            ) : (
              <Form.FieldMappingSelectMulti
                dataTestid={valueTestid}
                key={isListResolved ? 'resolved' : 'loading'}
                id={valueId}
                label={t('valuesLabel')}
                options={valueOptions}
                variableGroups={variableGroups}
                fieldMappingId={fieldMappingId ?? ''}
                validators={validators}
                defaultValue={reconciledMultiValue.value as FieldMappingSelectMultiValue}
              />
            )}
            {!allowEmpty && isMultiValueStale && (
              <FormError
                text={t('staleValues', { values: multiValueReconciled.staleLabels.join(', ') })}
              />
            )}
          </ComparatorValueContainer>
          <WebhookAllowEmpty />
        </Stack>
      );
    case 'boolean':
      return (
        <ComparatorValueContainer>
          <Form.Select
            id={comparatorId}
            options={applyFilter(BOOLEAN_COMPARATORS)}
            label={t('comparatorLabel')}
            defaultValue={condition.comparator}
            validators={validators}
            isClearable={false}
            onChange={setSelectedComparator}
          />
        </ComparatorValueContainer>
      );
    case 'file':
      return (
        <Stack space={4}>
          <ComparatorValueContainer>
            <Form.Select
              id={comparatorId}
              options={applyFilter(FILE_COMPARATORS)}
              label={t('comparatorLabel')}
              defaultValue={condition.comparator}
              validators={validators}
              isClearable={false}
              onChange={setSelectedComparator}
            />
            {allowEmpty ? <EmptyDisabledInput label={t('valueLabel')} /> : textValueField}
          </ComparatorValueContainer>
          <WebhookAllowEmpty />
        </Stack>
      );
    case 'number':
      return (
        <Stack space={4}>
          <ComparatorValueContainer>
            <Form.Select
              id={comparatorId}
              options={applyFilter(NUMBER_COMPARATORS)}
              label={t('comparatorLabel')}
              defaultValue={condition.comparator}
              validators={validators}
              isClearable={false}
              onChange={setSelectedComparator}
            />
            {allowEmpty ? (
              <EmptyDisabledInput label={t('valueLabel')} />
            ) : (
              <Form.FieldMappingNumberField
                dataTestid={valueTestid}
                id={valueId}
                label={t('valueLabel')}
                variableGroups={variableGroups}
                defaultValue={defaultVariableValue}
                validators={validators}
                fieldMappingId={fieldMappingId ?? ''}
                isDecimal={mappedField?.type === 'decimal'}
              />
            )}
          </ComparatorValueContainer>
          <WebhookAllowEmpty />
        </Stack>
      );
    default:
      return (
        <Stack space={4}>
          <ComparatorValueContainer>
            <Form.Select
              id={comparatorId}
              options={applyFilter(BASE_COMPARATORS)}
              label={t('comparatorLabel')}
              defaultValue={condition.comparator}
              validators={validators}
              isClearable={false}
              onChange={setSelectedComparator}
            />
            {allowEmpty ? <EmptyDisabledInput label={t('valueLabel')} /> : textValueField}
          </ComparatorValueContainer>
          <WebhookAllowEmpty />
        </Stack>
      );
  }
};

export const StaleConditionValueError = ({
  isMulti,
  staleLabels,
}: {
  isMulti: boolean;
  staleLabels: string[];
}) => {
  const { t } = useScopedTranslate(locale);
  return (
    <FormError
      text={isMulti ? t('staleValues', { values: staleLabels.join(', ') }) : t('staleValue')}
    />
  );
};

const locale = {
  en: {
    comparatorLabel: 'Comparator',
    valueLabel: 'Value',
    valuesLabel: 'Values',
    dateLabel: 'Date',
    fieldmapInJourney: 'Select value in journey builder',
    staleValue: "This value is no longer one of the field's options. Choose a new value.",
    staleValues:
      "These values are no longer among the field's options: {values}. Choose new values.",
  },
};

export const ComparatorValueContainer = styled(Stack).attrs({
  orientation: 'vertical',
  space: 2,
})({});
